import Fuse from 'fuse.js';

export default () => ({
  query: '',
  minChars: 3,
  activeSection: null,
  searching: false,
  faq: null,
  results: [],
  show: true,
  observer: null,
  hashChangeHandlerAdded: false,
  fuseOptions: {
    minMatchCharLength: 3,
    findAllMatches: true,
    threshold: 0.3,
    ignoreLocation: true,
    keys: ['title', 'answer'],
  },

  slugify(str) {
    str = str.replace(/^\s+|\s+$/g, '');
    str = str.toLowerCase();

    var from =
      'ÁÄÂÀÃÅČÇĆĎÉĚËÈÊẼĔȆĞÍÌÎÏİŇÑÓÖÒÔÕØŘŔŠŞŤÚŮÜÙÛÝŸŽáäâàãåčçćďéěëèêẽĕȇğíìîïıňñóöòôõøðřŕšşťúůüùûýÿžþÞĐđßÆa·/_,:;';
    var to =
      'AAAAAACCCDEEEEEEEEGIIIIINNOOOOOORRSSTUUUUUYYZaaaaaacccdeeeeeeeegiiiiinnooooooorrsstuuuuuyyzbBDdBAa------';
    for (var i = 0, l = from.length; i < l; i++) {
      str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
    }

    str = str
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

    return str;
  },

  matches(text) {
    if (!text) return this.query.trim().length <= this.minChars;
    if (this.query.trim().length <= this.minChars) return true;
    const q = this.slugify(this.query);
    const t = this.slugify(String(text));
    return t.includes(q);
  },

  scrollMobileNavToActive(sectionId) {
    if (window.innerWidth > 991) return;

    const mobileNav = document.querySelector('.content-and-nav__nav-wrapper--mobile .content-and-nav__nav');
    if (!mobileNav) return;

    const activeItem = mobileNav.querySelector(`a[href="#${sectionId}"]`)?.parentElement;
    if (!activeItem) return;

    const navRect = mobileNav.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    const scrollLeft = activeItem.offsetLeft - (navRect.width / 2) + (itemRect.width / 2);

    mobileNav.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  },

  filter() {
    if (!this.query || this.query.length < this.minChars) {
      this.results = [];
      this.searching = false;
      this.showAllCategories();
      this.$nextTick(() => {
        this.observeHeadings();
      });
      return;
    }

    this.searching = true;
    this.results = [];

    if (this.faq) {
      this.faq.forEach((section) => {
        const fuse = new Fuse(section.questions, this.fuseOptions);
        const results = fuse.search(this.query);
        const resSection = {
          section_title: section.section_title,
          section_slug: this.slugify(section.section_title),
          questions: results.map((question) => question.item),
        };
        if (results.length > 0) {
          this.results.push(resSection);
        }
      });
      const sections = this.results.map((item) => item.section_title);
      this.$dispatch('set-sidenav', sections);

      this.updateActiveSectionFromResults();

      this.$nextTick(() => {
        this.observeHeadings();
      });
    }

    this.searching = false;
  },

  showAllCategories() {
    this.$dispatch('set-sidenav', []);
  },

  updateSidebarVisibility(visibleSections) {
    const sidebarLinks = this.$root.querySelectorAll('.faq__sections a');
    sidebarLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (href && href.startsWith('#')) {
        const sectionId = href.substring(1);
        const shouldShow = this.query.trim().length <= this.minChars || visibleSections.has(sectionId);
        link.style.display = shouldShow ? '' : 'none';
      }
    });
  },

  setFromHash() {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      this.activeSection = hash;
    }
  },

  observeHeadings() {
    if (this.observer) {
      this.observer.disconnect();
    }

    const sections = Array.from(
      this.$root.querySelectorAll('.section--faq[id]')
    );

    if (sections.length === 0) {
      setTimeout(() => this.observeHeadings(), 200);
      return;
    }

    this.setFromHash();
    if (!window.location.hash && sections[0] && sections[0].id) {
      this.activeSection = sections[0].id;
      setTimeout(() => {
        this.scrollMobileNavToActive(this.activeSection);
      }, 100);
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        let bestEntry = null;
        let bestRatio = 0;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > bestRatio) {
            bestEntry = entry;
            bestRatio = entry.intersectionRatio;
          }
        });

        if (bestEntry) {
          const newSection = bestEntry.target.id;
          if (this.activeSection !== newSection) {
            this.activeSection = newSection;
            history.replaceState(null, '', `#${this.activeSection}`);

            this.$nextTick(() => {
              this.scrollMobileNavToActive(this.activeSection);
            });
          }
        }
      },
      {
        root: null,
        rootMargin: '-20% 0px -60% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    sections.forEach((section) => this.observer.observe(section));

    if (!this.hashChangeHandlerAdded) {
      window.addEventListener('hashchange', () => {
        this.setFromHash();
        this.$nextTick(() => {
          this.scrollMobileNavToActive(this.activeSection);
        });
      });
      this.hashChangeHandlerAdded = true;
    }
  },

  updateActiveSectionFromResults() {
    if (this.query.length >= 3 && this.results.length > 0) {
      const firstSection = this.results[0];
      if (firstSection && firstSection.section_slug) {
        this.activeSection = firstSection.section_slug;
      }
    }
  },

  loadFromDOM() {
    setTimeout(() => {
      this.extractDataFromDOM();
    }, 100);
  },

  extractDataFromDOM() {
    const sections = [];

    const sectionElements = this.$root.querySelectorAll('.section--faq');

    if (sectionElements.length === 0) {
      setTimeout(() => this.extractDataFromDOM(), 200);
      return;
    }

    sectionElements.forEach((sectionEl) => {
      const titleEl = sectionEl.querySelector('.section__header h3');
      const questionElements = sectionEl.querySelectorAll('.faq__item');

      if (titleEl && questionElements.length > 0) {
        const sectionTitle = titleEl.textContent.trim();
        const questions = [];

        questionElements.forEach((questionEl) => {
          const questionTitle = questionEl.querySelector('h3 a');
          const answerContent = questionEl.querySelector('.faq__item__content');

          if (questionTitle && answerContent) {
            questions.push({
              title: questionTitle.textContent.trim(),
              answer: answerContent.textContent.trim()
            });
          }
        });

        if (questions.length > 0) {
          sections.push({
            section_title: sectionTitle,
            questions: questions
          });
        }
      }
    });

    this.faq = sections;
  },

  init() {
    this.showAllCategories();
    this.observeHeadings();
    this.loadFromDOM();
  },
});
